import { useMemo, type PropsWithChildren } from "react";
import { type HttpError, useTable, useTranslate, useGo, useNavigation, useCustomMutation, useNotification } from "@refinedev/core";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { IContact } from "@/utils/zod/contactFormValidation";
import { CreateButton, DeleteButton } from "@refinedev/mui";
import { Stack, IconButton, Typography, Tooltip } from "@mui/material";
import { EditOutlined as EditOutlinedIcon, Replay } from "@mui/icons-material";
import { RefineListView } from "@/components/refine-list-view";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation } from "react-router";
import CustomChip from "@/components/override/Chip";
import { useTableState } from "@/hooks/useTableState";

export const TeamMemberList = ({ children }: PropsWithChildren) => {
    const t = useTranslate();
    const { createUrl } = useNavigation();
    const go = useGo();
    const { pathname } = useLocation();
    const methods = useForm<IContact>();
    const { tableState, onColumnFiltersChange, onGlobalFilterChange, onSortingChange } = useTableState<IContact>();

    const { tableQuery } = useTable<IContact[], HttpError, IContact>({
        resource: "accounts/team-members",
    });
    const teamMembers = tableQuery?.data?.data ?? [];

    const { mutate: resetDeviceId } = useCustomMutation();
    const { open } = useNotification();

    const columns = useMemo<MRT_ColumnDef<IContact>[]>(
        () => [
            {
                accessorKey: 'id',
                header: 'ID',
                size: 50,
                Cell: ({ row }) => {
                    return (
                        <Typography>
                            {row.index + 1}
                        </Typography>
                    );
                }
            },
            {
                accessorKey: 'name',
                header: 'Name',
                size: 160,
                Cell: ({ row }) => {
                    return (
                        <Typography>
                            {(row.original.name && row.original.user?.name) ? `${row.original.name} (${row.original.user?.name})` : row.original.name ? row.original.name : row.original.user?.name ? `(${row.original.user?.name})` : ''}
                        </Typography>
                    );
                }
            },
            {
                accessorKey: 'user.phone_number',
                header: 'Phone Number',
                size: 160,
            },
            {
                accessorKey: 'designation',
                header: 'Designation',
                size: 160,
            },
            {
                accessorKey: 'role_permission_name',
                header: 'Role',
                size: 160,
            },
            {
                accessorKey: 'notes',
                header: 'Notes',
                size: 160,
            },
            {
                accessorKey: 'status',
                header: 'Status',
                size: 160,
                Cell: ({ row }) => {
                    return (
                        <CustomChip label={row.original.status} status={row.original.status === "approved" ? "positive" : row.original.status === "pending" ? "neutral" : "negative"} type="status" />
                    );
                }
            },
            {
                accessorKey: 'actions',
                header: 'Actions',
                size: 100,
                enableSorting: false,
                enableColumnFilter: false,
                Cell: ({ row }) => (
                    <Stack direction="row" spacing={1}>
                        <IconButton
                            color="primary"
                            onClick={() => {
                                go({
                                    to: `/accounts/team-members/${row.original.id}/edit`,
                                    query: {
                                        to: pathname,
                                        resource: "accounts",
                                        id: row.original.id
                                    },
                                });
                            }}
                        >
                            <EditOutlinedIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                        <DeleteButton
                            hideText
                            resource="accounts/team-members"
                            recordItemId={row.original.id}
                        />
                        <Tooltip title="Reset Device ID">
                            <IconButton onClick={() => {
                                resetDeviceId({
                                    url: `/accounts/reset-device-id/${row.original.id}`,
                                    method: "patch",
                                    values: {},
                                }, {
                                    onSuccess: () => {
                                        open?.({
                                            message: "Device ID reset successfully",
                                            type: "success",
                                        });
                                    },
                                    onError: () => {
                                        open?.({
                                            message: "Failed to reset device ID",
                                            type: "error",
                                        });
                                    },
                                })
                            }}>
                                <Replay fontSize="small" sx={{ mr: 1 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ),
            },
        ],
        [t, methods]
    );

    return (
        <>
            <RefineListView headerButtons={
                (props) => [
                    <CreateButton
                        {...props.createButtonProps}
                        key="create"
                        size="medium"
                        sx={{ height: "40px" }}
                        onClick={() => {
                            return go({
                                to: `${createUrl("accounts/team-members")}`,
                                query: {
                                    to: pathname,
                                },
                                options: {
                                    keepQuery: true,
                                },
                                type: "replace",
                            });
                        }}
                    >
                        {t("team-members.titles.create")}
                    </CreateButton>
                ]
            }>
                <FormProvider {...methods}>
                    <MaterialReactTable
                        columns={columns}
                        data={teamMembers}
                        enableTopToolbar
                        enableBottomToolbar
                        enableColumnFilters
                        enableGlobalFilter
                        enablePagination
                        enableColumnPinning
                        muiTableBodyRowProps={{ hover: true }}
                        muiPaginationProps={{
                            rowsPerPageOptions: [10, 20, 50, 100],
                            showFirstButton: true,
                            showLastButton: true,
                        }}
                        state={{
                            ...tableState,
                            isLoading: tableQuery?.isLoading,
                        }}
                        onColumnFiltersChange={onColumnFiltersChange}
                        onGlobalFilterChange={onGlobalFilterChange}
                        onSortingChange={onSortingChange}
                        enableColumnResizing
                        enableFullScreenToggle
                        enableDensityToggle
                        enableGlobalFilterModes
                        positionGlobalFilter="right"
                        getRowId={(row) => row.id?.toString() ?? ''}
                    />
                </FormProvider>
            </RefineListView>
            {children}
        </>
    );
};
